<script lang="ts">
    import { navigate } from '../lib/router.svelte';
  import AdminShell from './AdminShell.svelte';
  import PageContainer from '../lib/ui/PageContainer.svelte';
  import StateBlock from '@ui/StateBlock.svelte';
  import PageHeader from '../lib/ui/PageHeader.svelte';
  import {
    redirectIfAwaitingOnboarding,
    getToken,
    handle401,
    type AdminUser,
  } from '../lib/admin';
  import { community, ensureCommunities, withCommunity, onCommunityChange } from '../lib/community.svelte';
  import { asAdminUser, ensureBootstrap } from '../lib/session';
  import { fetchClassroom, patchClassroomAll, patchClassroomNodes, type Classroom, type ClassroomNodeKind } from '@api';

  let me = $state<AdminUser | null>(null);
  let tree = $state<Classroom | null>(null);
  let loading = $state(true);
  let treeLoading = $state(false);
  let bulkBusy = $state(false);
  let error = $state<string | null>(null);
  let toast = $state<string | null>(null);
  // The community whose tree is on screen — so the community-change effect below
  // doesn't re-issue the request `init` already has in flight.
  // Keys (`kind:id`) currently mid-toggle, to disable their switch.
  let busy = $state<Record<string, boolean>>({});

  function key(kind: ClassroomNodeKind, id: number): string {
    return `${kind}:${id}`;
  }

  function resourceStatus(s: string | null): { label: string; cls: string } {
    switch (s) {
      case 'ok':
        return { label: 'indexed', cls: 'text-success' };
      case 'unavailable':
        return { label: 'private · can’t fetch', cls: 'text-warning/80' };
      case 'unsupported':
        return { label: 'unsupported type', cls: 'text-base-content/62' };
      case 'failed':
        return { label: 'fetch failed', cls: 'text-error/80' };
      default:
        return { label: 'not fetched', cls: 'text-base-content/54' };
    }
  }

  // Deep links back into Skool / out to the resource, opened in a new tab.
  const SKOOL = 'https://www.skool.com';
  function courseUrl(slug: string | null): string {
    return `${SKOOL}/${tree?.community.slug}/classroom/${slug}`;
  }
  function lessonUrl(courseSlug: string | null, pageExternalId: string | null): string | null {
    return pageExternalId ? `${SKOOL}/${tree?.community.slug}/classroom/${courseSlug}?md=${pageExternalId}` : null;
  }

  // The header's five integers, counted server-side. This used to be a triple-nested
  // loop over the whole tree re-run on every render, and a bulk toggle re-downloaded
  // the tree purely so it could be re-run (doc 02 W8); every PATCH now answers with
  // the new totals, so the only thing that ever assigns them is a server response.
  const stats = $derived(tree?.totals ?? null);

  /**
   * The cold load, in ONE round trip (doc 04 §4.1).
   *
   * `fetchMe()` → `ensureCommunities()` → the tree was two round trips before the
   * tree was even requested, for a community id that was in `localStorage` the whole
   * time. `withCommunity` issues the classroom request immediately against that id,
   * in parallel with the bootstrap that confirms it, and retries once if the server
   * says the id is stale.
   *
   * The bootstrap is awaited first although both were issued together: an account
   * still in onboarding must be redirected rather than shown a data error, and the
   * classroom request will legitimately fail for exactly that account.
   */
  async function init(): Promise<void> {
    if (!getToken()) {
      navigate('/admin/login');
      return;
    }
    error = null;
    let pageErr: unknown = null;
    const pending = withCommunity((skoolId) => fetchClassroom(skoolId)).catch(
      (err: unknown) => {
        pageErr = err;
        return null;
      },
    );
    try {
      me = asAdminUser(await ensureBootstrap());
      if (redirectIfAwaitingOnboarding(me, navigate)) return;
      await ensureCommunities();
      // No community at all: the empty state, not an error — `withCommunity` rejects
      // in that case and its rejection is the one thing we deliberately swallow.
      if (community.selectedId == null) return;
      const loaded = await pending;
      if (loaded) {
        tree = loaded;
        watch.seen(community.selectedId);
      } else if (pageErr) {
        throw pageErr;
      }
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      error = err instanceof Error ? err.message : 'Failed to load.';
    } finally {
      loading = false;
    }
  }

  // Reload the tree when the shared community selection changes. The first community
  // is already on screen from `init`, so it is skipped here (`loading` is true only
  // while `init` is in flight) — without that guard this fires the moment the
  // bootstrap lands and re-issues the request already on the wire.
  const watch = onCommunityChange((id) => void loadTree(id), { gate: () => !loading });

  async function loadTree(skoolId: number): Promise<void> {
    treeLoading = true;
    error = null;
    try {
      tree = await fetchClassroom(skoolId);
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      error = err instanceof Error ? err.message : 'Failed to load content.';
      tree = null;
    } finally {
      treeLoading = false;
      loading = false;
    }
  }

  /**
   * Enable or disable everything — ONE request (doc 02 W8).
   *
   * The old chain was `POST …/toggle-all` → `GET /admin/content-tree`: the whole
   * course/section/page/resource tree re-downloaded to flip a boolean everywhere the
   * client could already flip it. `PATCH …/classroom` returns the new totals, so the
   * tree we hold is updated in place and the header comes from the server.
   */
  async function onToggleAll(enabled: boolean): Promise<void> {
    const id = community.selectedId;
    const t = tree;
    if (id == null || t == null || bulkBusy) return;
    bulkBusy = true;
    try {
      const res = await patchClassroomAll(id, enabled);
      for (const course of t.courses) {
        course.enabled = enabled;
        for (const section of course.sections) {
          section.enabled = enabled;
          for (const page of section.pages) {
            page.enabled = enabled;
            for (const resource of page.resources) resource.enabled = enabled;
          }
        }
      }
      t.totals = res.totals;
    } catch (err: unknown) {
      if (!handle401(err, navigate)) {
        toast = err instanceof Error ? err.message : 'Could not save.';
        setTimeout(() => (toast = null), 3000);
      }
    } finally {
      bulkBusy = false;
    }
  }

  // Optimistic toggle: flip locally, persist, revert on failure. The response carries
  // the new totals, so the header stays honest without a re-read.
  async function onToggle(
    kind: ClassroomNodeKind,
    node: { id: number; enabled: boolean },
    value: boolean,
  ): Promise<void> {
    const id = community.selectedId;
    if (id == null) return;
    const k = key(kind, node.id);
    const prev = node.enabled;
    node.enabled = value;
    busy = { ...busy, [k]: true };
    try {
      const res = await patchClassroomNodes(id, [{ kind, id: node.id, enabled: value }]);
      if (tree) tree.totals = res.totals;
    } catch (err: unknown) {
      if (!handle401(err, navigate)) {
        node.enabled = prev;
        toast = err instanceof Error ? err.message : 'Could not save.';
        setTimeout(() => (toast = null), 3000);
      }
    } finally {
      const { [k]: _drop, ...rest } = busy;
      busy = rest;
    }
  }

  void init();
</script>

<AdminShell {me}>

  {#if loading}
    <StateBlock state="loading" />
  {:else if error && !tree}
    <StateBlock state="error" message={error} onretry={init} />
  {:else}
    <PageContainer>
      <!-- The eyebrow said "Sideguide AI · Settings". There is no Settings node in the
           navigation — this page is the "Indexed Content" leaf under Sideguide AI, and
           inventing a parent for it put the reader somewhere the sidebar cannot go.
           The corpus counts are an adornment of the title, and "Set all" is the page's
           action, so both sit on the title's baseline instead of below it. -->
      <PageHeader section="Sideguide AI" title="Indexed Content">
        {#snippet titleExtra()}
          {#if stats}
            <span class="flex flex-wrap gap-x-2 font-mono text-[0.7rem] font-normal text-base-content/65">
              <span>{stats.courses} courses</span>
              <span>· {stats.sections} sections</span>
              <span>· {stats.pages} lessons</span>
              {#if stats.off > 0}<span class="text-warning/80">· {stats.off} off</span>{/if}
              {#if stats.locked > 0}<span class="text-warning/80" title="Tier-gated — Sideguide's account can't access these">· {stats.locked} locked</span>{/if}
            </span>
          {/if}
        {/snippet}
        {#snippet actions()}
          {#if tree && tree.courses.length > 0}
            <div class="flex items-center gap-2">
              <span class="eyebrow">Set all</span>
              <div class="join">
                <button class="btn join-item btn-sm" disabled={bulkBusy} onclick={() => onToggleAll(true)}>
                  {#if bulkBusy}<span class="loading loading-spinner loading-xs"></span>{/if}All on
                </button>
                <button class="btn join-item btn-sm" disabled={bulkBusy} onclick={() => onToggleAll(false)}>All off</button>
              </div>
            </div>
          {/if}
        {/snippet}
      </PageHeader>
      {#if treeLoading}
        <StateBlock state="loading" compact />
      {:else if !tree || tree.courses.length === 0}
        <StateBlock state="empty" message="Nothing indexed" hint="No courses are indexed for this community yet." compact />
      {:else}
        {#snippet extLink(href: string, label: string)}
          <a
            {href}
            target="_blank"
            rel="noopener noreferrer"
            class="text-base-content/54 transition-colors hover:text-primary"
            title={label}
            aria-label={label}
            onclick={(e) => e.stopPropagation()}
          >
            <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
          </a>
        {/snippet}
        {#snippet lockedBadge()}
          <span
            class="eyebrow inline-flex items-center gap-1 rounded-full border border-warning/35 bg-warning/10 px-1.5 py-0.5 normal-case text-warning/90"
            title="Tier-gated on Skool — Sideguide's account can't access this, so it isn't indexed."
          >
            <svg class="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            Locked
          </span>
        {/snippet}
        <div class="flex flex-col gap-2.5">
          {#each tree.courses as course (course.id)}
            {@const cOn = course.enabled}
            <details
              class="group/c overflow-hidden rounded-box border hair bg-base-100 transition-colors
                {cOn ? 'border-l-[3px] border-l-primary/70' : 'border-l-[3px] border-l-base-300'}"
            >
              <summary class="flex cursor-pointer list-none items-center gap-3 px-4 py-3">
                <input
                  type="checkbox"
                  class="toggle toggle-sm toggle-primary"
                  checked={course.enabled}
                  disabled={busy[`course:${course.id}`]}
                  onclick={(e) => e.stopPropagation()}
                  onchange={(e) => onToggle('course', course, e.currentTarget.checked)}
                />
                <span class="redact font-medium {cOn && !course.locked ? '' : 'text-base-content/65'}">{course.title}</span>
                {#if course.locked}{@render lockedBadge()}{/if}
                <span class="ml-auto flex items-center gap-3">
                  <span class="font-mono text-[0.7rem] text-base-content/62">{course.sections.length} sections</span>
                  {@render extLink(courseUrl(course.slug), 'Open course in Skool')}
                  <svg class="h-4 w-4 text-base-content/58 transition-transform group-open/c:rotate-90" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m9 18 6-6-6-6"/></svg>
                </span>
              </summary>

              <div class="flex flex-col gap-1 px-4 pb-3 pl-5">
                {#if course.locked && course.sections.length === 0}
                  <p class="py-1.5 text-sm text-base-content/65">
                    This course is tier-gated on Skool — Sideguide's account can't access it, so none of its lessons are indexed.
                  </p>
                {/if}
                {#each course.sections as section (section.id)}
                  {@const sBlocked = !course.enabled}
                  {@const sOn = course.enabled && section.enabled}
                  <details class="group/s rail {sOn ? 'rail-on' : ''} pl-3">
                    <summary class="flex cursor-pointer list-none items-center gap-3 py-2">
                      <input
                        type="checkbox"
                        class="toggle toggle-xs toggle-primary {sBlocked ? 'opacity-40' : ''}"
                        checked={section.enabled}
                        disabled={busy[`section:${section.id}`] || sBlocked}
                        onclick={(e) => e.stopPropagation()}
                        onchange={(e) => onToggle('section', section, e.currentTarget.checked)}
                      />
                      <span class="redact text-sm {sOn ? '' : 'text-base-content/65'}">{section.name}</span>
                      {#if sBlocked}<span class="eyebrow normal-case text-base-content/62">off via course</span>{/if}
                      <span class="ml-auto flex items-center gap-2.5">
                        <span class="font-mono text-[0.7rem] text-base-content/62">{section.pages.length} lessons</span>
                        <svg class="h-3.5 w-3.5 text-base-content/54 transition-transform group-open/s:rotate-90" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m9 18 6-6-6-6"/></svg>
                      </span>
                    </summary>

                    <div class="rail {sOn ? 'rail-on' : ''} ml-1.5 flex flex-col pl-3">
                      {#each section.pages as page (page.id)}
                        {@const pBlocked = !(course.enabled && section.enabled)}
                        {@const pOn = course.enabled && section.enabled && page.enabled}
                        {@const lu = lessonUrl(course.slug, page.external_id)}
                        <div class="flex flex-col">
                          <div class="flex items-center gap-3 py-1.5">
                            <input
                              type="checkbox"
                              class="toggle toggle-xs toggle-primary {pBlocked ? 'opacity-40' : ''}"
                              checked={page.enabled}
                              disabled={busy[`page:${page.id}`] || pBlocked}
                              onchange={(e) => onToggle('page', page, e.currentTarget.checked)}
                            />
                            <span class="redact text-sm {pOn && !page.locked ? 'text-base-content/85' : 'text-base-content/62'}">{page.title}</span>
                            {#if page.locked}{@render lockedBadge()}{/if}
                            {#if pBlocked && !page.locked}<span class="eyebrow normal-case text-base-content/62">off via parent</span>{/if}
                            {#if lu}<span class="ml-auto">{@render extLink(lu, 'Open lesson in Skool')}</span>{/if}
                          </div>
                          {#if page.resources.length > 0}
                            <div class="mb-1 flex flex-col gap-0.5 pl-8">
                              {#each page.resources as r (r.id)}
                                {@const st = resourceStatus(r.content_status)}
                                {@const indexed = r.content_status === 'ok'}
                                {@const used = pOn && r.enabled && indexed}
                                {@const rBlocked = !pOn}
                                <div class="flex items-center gap-2.5 py-0.5 {indexed ? '' : 'opacity-60'}">
                                  <input
                                    type="checkbox"
                                    class="toggle toggle-xs toggle-primary {rBlocked ? 'opacity-40' : ''}"
                                    checked={r.enabled}
                                    disabled={busy[`resource:${r.id}`] || !indexed || rBlocked}
                                    onchange={(e) => onToggle('resource', r, e.currentTarget.checked)}
                                  />
                                  {#if r.link}
                                    <a
                                      href={r.link}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      class="truncate text-xs text-base-content/72 transition-colors hover:text-primary hover:underline"
                                    >📎 <span class="redact">{r.title}</span></a>
                                  {:else}
                                    <span class="redact truncate text-xs text-base-content/72">📎 {r.title}</span>
                                  {/if}
                                  {#if !indexed}
                                    <span class="ml-auto font-mono text-[0.65rem] {st.cls}" title="This resource’s content was not added to the index">{st.label}</span>
                                  {:else if used}
                                    <span class="ml-auto font-mono text-[0.65rem] text-success" title="Fetched, indexed, and being used in answers">● in answers</span>
                                  {:else}
                                    <span class="ml-auto font-mono text-[0.65rem] text-base-content/62" title="Indexed, but switched off (or its lesson/course is off)">indexed · off</span>
                                  {/if}
                                </div>
                              {/each}
                            </div>
                          {/if}
                        </div>
                      {/each}
                    </div>
                  </details>
                {/each}
              </div>
            </details>
          {/each}
        </div>
      {/if}
    </PageContainer>
  {/if}

  {#if toast}
    <div class="toast toast-end">
      <div class="rounded-box border border-error/30 bg-error/15 px-4 py-3 text-sm text-error shadow-lg">
        {toast}
      </div>
    </div>
  {/if}
</AdminShell>
